import { Add } from "@mui/icons-material";
import { Autocomplete, Fab, MenuItem, Select, SelectChangeEvent, Stack, TextField, Tooltip } from "@mui/material";
import { GridColDef, GridRenderCellParams } from "@mui/x-data-grid";
import { parseAsString } from "nuqs";
import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";

import { GqlClientUser } from "~/__generated__";
import GaiaDataList from "~/modules/app/components/GaiaDataList";
import GaiaDeleteButton from "~/modules/app/components/GaiaDeleteButton";
import GaiaPageGridComponent from "~/modules/app/components/GaiaPageGridComponent";
import GaiaPageLayoutColumn from "~/modules/app/components/GaiaPageLayoutColumn";
import ErrorScreen from "~/modules/app/screens/ErrorScreen";
import useRequireCurrentClient from "~/modules/client/hooks/useRequireCurrentClient";
import UserBadge from "~/modules/user/components/UserBadge";
import SearchInput from "~/shared/components/SearchInput";
import { useDebouncedValue } from "~/shared/hooks/useDebouncedValue";
import { useGridQueryState } from "~/shared/hooks/useGridQueryState";

import useClientUserAssignRole from "../api/useClientUserAssignRole";
import useClientUserDelete from "../api/useClientUserDelete";
import useClientUserGetAllRoles from "../api/useClientUserGetAllRoles";
import useClientUserGetAllWithAccessToClient from "../api/useClientUserGetAllWithAccessToClient";

export default function ClientUserListScreen() {
    const { t } = useTranslation("user");
    const { client } = useRequireCurrentClient();

    const [queryState, setQueryState, gridModelProps] = useGridQueryState({
        q: parseAsString.withDefault(""),
        role: parseAsString.withDefault(""),
    });

    const debouncedQ = useDebouncedValue(queryState.q);

    const params = useMemo(
        () => ({
            q: debouncedQ || undefined,
            role: queryState.role || undefined,
            page: queryState.page,
            pageSize: queryState.pageSize,
            sortBy: queryState.sortBy,
            sortOrder: queryState.sortOrder,
        }),
        [debouncedQ, queryState],
    );

    const { data, loading, error, totalRows } = useClientUserGetAllWithAccessToClient(params, client);

    const { deleteClientUser, loading: deleteLoading } = useClientUserDelete();
    const handleDelete = useCallback(
        (userId: string) => deleteClientUser({ clientId: client.id, userId }),
        [client.id, deleteClientUser],
    );

    const { allRoles } = useClientUserGetAllRoles();
    const { assignRole } = useClientUserAssignRole();

    const roleOptions = useMemo(() => allRoles.map(role => ({ label: role.name, value: role.id })), [allRoles]);

    const handleParameterChange = <T,>(key: string, value: T) => {
        setQueryState({ [key]: value, page: 0 } as Parameters<typeof setQueryState>[0]);
    };

    const columns: GridColDef[] = useMemo(
        () => [
            {
                field: "user",
                headerName: t("userListScreen.colName"),
                renderCell: (params: GridRenderCellParams<GqlClientUser>) => {
                    const user = params.row.user;
                    if (!user) return null;
                    return (
                        <UserBadge
                            names={[user.firstName, user.lastName]}
                            profilePicture={user.profilePictureMedia?.url}
                            showName={true}
                        />
                    );
                },
                flex: 1,
            },
            {
                field: "email",
                headerName: t("userListScreen.colEmail"),
                valueGetter: (_, row) => row.user?.email,
                flex: 1,
            },
            {
                field: "phone",
                headerName: t("userListScreen.colPhone"),
                valueGetter: (_, row) => row.user?.phone,
                flex: 1,
            },
            {
                field: "role",
                headerName: t("userListScreen.colRole"),
                flex: 1,
                sortable: false,
                renderCell: params => {
                    const handleChange = async (event: SelectChangeEvent) => {
                        await assignRole(client.id, { userId: params.row.userId, roleId: event.target.value });
                    };

                    return (
                        <Select
                            size="small"
                            fullWidth
                            value={params.row.roleId}
                            onChange={handleChange}
                            onClick={e => e.stopPropagation()}
                        >
                            {allRoles.map(role => (
                                <MenuItem key={role.id} value={role.id}>
                                    {role.name}
                                </MenuItem>
                            ))}
                        </Select>
                    );
                },
            },
            {
                field: "actions",
                headerName: "",
                sortable: false,
                filterable: false,
                disableColumnMenu: true,
                align: "right",
                renderCell: (params: GridRenderCellParams<GqlClientUser>) => (
                    <GaiaDeleteButton onDelete={() => handleDelete(params.row.userId)} isLoading={deleteLoading} />
                ),
            },
        ],
        [t, allRoles, assignRole, client.id, deleteLoading, handleDelete],
    );

    if (error) {
        return <ErrorScreen error={error} />;
    }

    return (
        <GaiaPageLayoutColumn maxWidth="xl">
            <GaiaPageGridComponent
                title={t("userListScreen.title")}
                actions={
                    <Tooltip title={t("userListScreen.tooltipNewUser")}>
                        <Fab component={Link} to="new" color="primary">
                            <Add />
                        </Fab>
                    </Tooltip>
                }
            >
                <Stack direction="row" gap={1} flexWrap="wrap" useFlexGap alignItems="flex-start" sx={{ flex: 1 }}>
                    <SearchInput
                        label={t("userListScreen.filterSearch")}
                        value={queryState.q}
                        onChange={value => handleParameterChange("q", value)}
                    />
                    <Autocomplete
                        size="small"
                        options={roleOptions}
                        value={roleOptions.find(o => o.value === queryState.role) ?? null}
                        onChange={(_, newValue) => handleParameterChange("role", newValue?.value ?? "")}
                        getOptionLabel={option => option.label}
                        getOptionKey={option => option.value}
                        isOptionEqualToValue={(option, val) => option.value === val.value}
                        renderInput={params => (
                            <TextField {...params} fullWidth label={t("userListScreen.filterRole")} variant="outlined" />
                        )}
                        sx={{ minWidth: "15%" }}
                    />
                </Stack>

                <GaiaDataList
                    data={data || []}
                    loading={loading}
                    error={error}
                    totalRows={totalRows || 0}
                    columns={columns}
                    gridModels={gridModelProps}
                />
            </GaiaPageGridComponent>
        </GaiaPageLayoutColumn>
    );
}
